import { rpeToRir, parseLocaleNumber, type EffortScale } from '@heraion/shared';

/** Canonical RIR from a set's two effort text fields: the resolver for display and migration reads.
 *  Not `savedRirOf`: that one mirrors what the SAVE accepts (integer rir 0..10,
 *  rpe 1..10, the athlete's scale first); this one takes any parseable value.
 *
 *  `rir` wins when present; a legacy `rpe` is projected. Returns null when
 *  neither parses.
 *
 *  ⚠️ `parseLocaleNumber`, never `Number`: the family types on it-IT / fr-FR /
 *  de-DE keyboards, where a half-step effort is written "8,5". `Number("8,5")`
 *  is NaN, so a hand-rolled parser here silently answers "no effort recorded"
 *  for a value that is plainly there — and every caller of this function acts
 *  on that answer (what the meter shows, what a migration decides to move).
 *  That is exactly the divergence two copies of this logic produced. */
export const canonicalRirOf = (
  rir: string | undefined,
  rpe: string | undefined,
): number | null =>
  canonicalRirFromNumbers(
    numOrUndefined(parseLocaleNumber(rir ?? '')),
    numOrUndefined(parseLocaleNumber(rpe ?? '')),
  );

const numOrUndefined = (n: number): number | undefined =>
  Number.isFinite(n) ? n : undefined;

/** The same rule when the two values arrive already parsed — the hydration
 *  paths read them off a stored/prescribed set, where they are numbers.
 *  Same precedence, same rounding: one rule, two doors. */
export const canonicalRirFromNumbers = (
  rir: number | undefined,
  rpe: number | undefined,
): number | null => {
  if (rir !== undefined && Number.isFinite(rir)) return Math.round(rir);
  if (rpe !== undefined && Number.isFinite(rpe)) return Math.round(rpeToRir(rpe));
  return null;
};

/** The canonical RIR `buildPayload` SAVES for a set's two effort cells, by the
 *  athlete's scale: the edited scale wins, the other is the fallback. rpe must
 *  be 1..10, rir an integer 0..10, else that cell is ignored. One copy for the save
 *  and the calibration ramp, which must read the same effort. */
export const savedRirOf = (
  rirText: string | undefined,
  rpeText: string,
  scale: EffortScale,
): number | null => {
  const rpeRaw = rpeText.trim();
  const rpeParsed = rpeRaw === '' ? null : parseLocaleNumber(rpeRaw);
  const rpeIn =
    rpeParsed !== null && Number.isFinite(rpeParsed) && rpeParsed >= 1 && rpeParsed <= 10
      ? rpeParsed
      : null;
  const rirRaw = (rirText ?? '').trim();
  const rirParsed = rirRaw === '' ? null : parseInt(rirRaw, 10);
  const rirIn =
    rirParsed !== null && Number.isFinite(rirParsed) && rirParsed >= 0 && rirParsed <= 10
      ? rirParsed
      : null;
  const canonical =
    scale === 'rir'
      ? (rirIn ?? (rpeIn !== null ? rpeToRir(rpeIn) : null))
      : (rpeIn !== null ? rpeToRir(rpeIn) : rirIn);
  return canonical === null ? null : Math.round(canonical);
};
